import { useState, useEffect, useRef } from 'react';
import { Moon, Sun, BookOpen, Layers, Users, LogOut, ChevronDown, BarChart3, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

const Layout = ({ children }) => {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const { user, effectiveRole, logout } = useAuth();
  const { darkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Menu por role - Superadmin vê ambos os menus
  const menuItems = effectiveRole === 'student' ? [
    { icon: BookOpen, label: 'Meus Materiais', href: '/student' },
  ] : effectiveRole === 'superadmin' ? [
    { icon: BarChart3, label: 'Dashboard', href: '/admin' },
    { icon: Layers, label: 'Módulos', href: '/admin/modules' },
    { icon: Users, label: 'Usuários', href: '/admin/students' },
    { icon: FileText, label: 'Relatórios', href: '/admin/reports' },
    { icon: BookOpen, label: 'Ver como Aluno', href: '/student' },
  ] : [
    { icon: BarChart3, label: 'Dashboard', href: '/admin' },
    { icon: Layers, label: 'Módulos', href: '/admin/modules' },
    { icon: Users, label: 'Usuários', href: '/admin/students' },
    { icon: FileText, label: 'Relatórios', href: '/admin/reports' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-background-900 text-gray-900 dark:text-text-primary">
      {/* Navbar Horizontal */}
      <motion.header
        initial={{ y: -64, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="h-14 bg-white dark:bg-background-800 border-b border-gray-200 dark:border-gray-700 flex items-center px-6 justify-between flex-shrink-0 shadow-sm"
      >
        {/* Logo e Menu */}
        <div className="flex items-center gap-8">
          <Link to={effectiveRole === 'student' ? '/student' : '/admin'} className="flex items-center gap-3">
            <img 
              src="/assets/logo.jpg" 
              alt="Mia Play" 
              className="h-8 w-auto object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <h1 className="text-xl font-bold text-primary-500" style={{ display: 'none' }}>Mia Play</h1>
          </Link>

          {/* Menu Horizontal */}
          <nav className="hidden md:flex items-center gap-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                    isActive
                      ? 'bg-primary-500/20 text-primary-500'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-background-700'
                  }`}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Busca e Ações */}
        <div className="flex items-center gap-4">
          {/* Tema */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleTheme}
            className="hover:bg-gray-100 dark:hover:bg-background-700 p-2 rounded-lg transition text-gray-700 dark:text-gray-300"
          >
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
          </motion.button>

          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-background-700 p-2 rounded-lg transition"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-accent-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                {user?.full_name?.charAt(0) || 'U'}
              </div>
              <div className="hidden lg:block text-left">
                <p className="text-sm font-medium text-gray-900 dark:text-text-primary leading-none">
                  {user?.full_name || 'Usuário'}
                </p>
                <p className="text-xs text-gray-600 dark:text-text-secondary capitalize">
                  {effectiveRole === 'superadmin' ? 'Super Admin' : effectiveRole}
                </p>
              </div>
              <ChevronDown size={16} className="text-gray-400 hidden lg:block" />
            </button>

            {/* Dropdown */}
            {userMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute right-0 mt-2 w-48 bg-white dark:bg-background-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50"
              >
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition w-full text-left text-sm"
                >
                  <LogOut size={18} className="text-red-500" />
                  <span className="text-red-500">Sair</span>
                </button>
              </motion.div>
            )}
          </div>
        </div>
      </motion.header>

      {/* Page Content */}
      <main className="flex-1 overflow-auto bg-gray-50 dark:bg-background-900">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {children}
        </motion.div>
      </main>
    </div>
  );
};

export default Layout;
